/**
 * Withdrawals & Payouts Articles — Spanish
 */

import type { HelpArticle } from "../../../types";

export const WITHDRAWALS_ARTICLES_ES: HelpArticle[] = [
  {
    id: "wd-001",
    slug: "payout-process-overview",
    title: "Resumen del proceso de pago",
    description: "Cómo funcionan los retiros a tu cuenta bancaria",
    category: "withdrawals",
    popular: true,
    order: 1,
    keywords: ["pago", "retiro", "banco", "proceso", "transferencia"],
    content: `
# Resumen del proceso de pago

Después de convertir tu cripto, así es como recibes fiat en tu cuenta bancaria.

## El flujo de pago

1. **Conversión completada** - Cripto convertido a fiat
2. **Saldo disponible** - Los fondos aparecen en tu cuenta
3. **Pago iniciado** - Automático o manual
4. **Transferencia bancaria** - Enviada por tu método configurado
5. **Fondos recibidos** - Dinero en tu cuenta bancaria

## Pagos automáticos vs manuales

### Pagos automáticos
- Fondos enviados inmediatamente después de la conversión
- No requiere acción
- Configura el umbral en ajustes
- Ideal para usuarios frecuentes

### Pagos manuales
- Solicita el retiro cuando quieras
- Controla el momento
- Consolida múltiples depósitos
- Bueno para montos grandes

## Métodos de pago

| Método | Regiones | Velocidad |
|--------|----------|-----------|
| ACH | EE.UU. | 2-3 días hábiles |
| Wire | EE.UU., Internacional | 1-2 días hábiles |
| SEPA | Europa | 1 día hábil |
| SPEI | México | Mismo día |

## Configurar pagos

1. Completa la verificación KYC
2. Añade los datos de tu cuenta bancaria
3. Verifica la titularidad de la cuenta
4. Configura las preferencias de pago
5. Comienza a recibir fondos

## Límites de pago

| Nivel | Límite diario | Límite mensual |
|-------|---------------|----------------|
| Estándar | $25,000 | $100,000 |
| Avanzado | $100,000 | $500,000 |
| Enterprise | Personalizado | Personalizado |
    `.trim(),
  },
  {
    id: "wd-002",
    slug: "adding-bank-accounts",
    title: "Añadir cuentas bancarias",
    description: "Cómo conectar tu cuenta bancaria para pagos",
    category: "withdrawals",
    popular: true,
    order: 2,
    keywords: ["banco", "cuenta", "añadir", "conectar", "configurar"],
    content: `
# Añadir cuentas bancarias

Conecta tu cuenta bancaria para recibir pagos en fiat.

## Requisitos

- Verificación KYC completada
- Cuenta bancaria a tu nombre
- La cuenta debe soportar transferencias entrantes

## Añadir una cuenta bancaria

### Paso 1: Navega a Ajustes
1. Ve a Ajustes > Pagos
2. Haz clic en "Añadir cuenta bancaria"
3. Selecciona tu país

### Paso 2: Introduce los datos

**Para cuentas en EE.UU. (ACH)**
- Número de routing (9 dígitos)
- Número de cuenta
- Tipo de cuenta (Corriente/Ahorro)
- Nombre del titular

**Para cuentas en EE.UU. (Wire)**
- Igual que ACH más
- Nombre del banco
- Dirección del banco

**Para cuentas europeas (SEPA)**
- IBAN
- BIC/SWIFT (opcional)
- Nombre del titular

**Para cuentas mexicanas (SPEI)**
- Número CLABE (18 dígitos)
- Nombre del banco
- Nombre del titular

### Paso 3: Verifica la cuenta
- Verificación por micro-depósitos (ACH)
- O verificación documental
- Normalmente 1-2 días hábiles

## Verificación de cuenta

### Micro-depósitos (ACH)
1. Enviamos dos depósitos pequeños
2. Revisa tu estado de cuenta
3. Introduce las cantidades en Supabank
4. ¡Cuenta verificada!

### Verificación documental
1. Sube un estado de cuenta
2. Debe mostrar tu nombre
3. Debe mostrar el número de cuenta
4. Verificado en 24 horas

## Múltiples cuentas

- Añade hasta 5 cuentas bancarias
- Establece una predeterminada para pagos
- Cambia de cuenta en cualquier momento
- Elimina cuentas cuando sea necesario

## Consejos

**Hazlo:**
- Usa cuentas a tu nombre legal
- Verifica todos los números dos veces
- Usa cuentas que accedas regularmente

**No hagas:**
- Usar cuentas de terceros
    `.trim(),
  },
  {
    id: "wd-003",
    slug: "supported-payout-currencies",
    title: "Monedas de pago soportadas",
    description: "Monedas disponibles para pagos bancarios",
    category: "withdrawals",
    order: 3,
    keywords: ["moneda", "fiat", "usd", "eur", "mxn"],
    content: `
# Monedas de pago soportadas

Recibe pagos en tu moneda fiat preferida.

## Monedas disponibles

| Moneda | Símbolo | Regiones |
|--------|---------|----------|
| Dólar estadounidense | USD | EE.UU., Internacional |
| Euro | EUR | Europa, Internacional |
| Peso mexicano | MXN | México |

## Selección de moneda

### Durante la conversión
- El cripto se convierte a fiat seleccionado
- Establece el predeterminado en preferencias
- Cambiar por transacción (próximamente)

### Moneda de pago
- Debe coincidir con la moneda de la cuenta bancaria
- O se aplica conversión de moneda

## Tasas de cambio

- Tasas de mercado en tiempo real
- Spreads competitivos
- Precios transparentes
- Sin cargos ocultos

## Pagos en USD

**ACH (Automated Clearing House)**
- Para cuentas bancarias de EE.UU.
- 2-3 días hábiles
- Comisiones bajas

**Transferencia Wire**
- Nacional e internacional
- 1-2 días hábiles
- Comisiones más altas, más rápido

## Pagos en EUR

**SEPA (Single Euro Payments Area)**
- Para cuentas europeas
- 1 día hábil
- Comisiones bajas

**Wire internacional**
- Países fuera de SEPA
- 2-3 días hábiles
- Comisiones estándar de wire

## Pagos en MXN

**SPEI (Sistema de Pagos Electrónicos Interbancarios)**
- Para cuentas mexicanas
- Mismo día (horario laboral)
- Comisiones muy bajas
    `.trim(),
  },
  {
    id: "wd-004",
    slug: "wire-vs-ach-vs-sepa",
    title: "Wire vs ACH vs SEPA",
    description: "Entendiendo las diferentes redes de pago",
    category: "withdrawals",
    order: 4,
    keywords: ["wire", "ach", "sepa", "transferencia", "pago"],
    content: `
# Wire vs ACH vs SEPA

Entiende las diferentes formas de recibir tus pagos.

## Comparación rápida

| Característica | ACH | Wire | SEPA |
|----------------|-----|------|------|
| Región | EE.UU. | Global | Europa |
| Velocidad | 2-3 días | 1-2 días | 1 día |
| Comisión | $0 | $15-25 | €0-3 |
| Límite | $25,000 | $100,000+ | €100,000 |
| Ideal para | Pagos regulares | Grandes/urgentes | Cuentas europeas |

## ACH (Automated Clearing House)

**Qué es:**
Red de pagos domésticos de EE.UU.

**Ventajas:**
- Gratis o muy bajo costo
- Confiable y establecido
- Bueno para pagos recurrentes

**Desventajas:**
- Tarda 2-3 días hábiles
- Solo cuentas de EE.UU.
- Límites más bajos por transacción

**Ideal para:**
- Pagos regulares, no urgentes
- Usuarios que buscan ahorro
- Cuentas bancarias de EE.UU.

## Transferencia Wire

**Qué es:**
Transferencia electrónica de banco a banco

**Ventajas:**
- Opción más rápida
- Límites más altos
- Capacidad internacional

**Desventajas:**
- Comisiones más altas ($15-25)
- Tu banco también puede cobrar
- Excesivo para montos pequeños

**Ideal para:**
- Pagos grandes
- Transferencias urgentes
- Cuentas internacionales

## SEPA (Single Euro Payments Area)

**Qué es:**
Integración de pagos europea

**Ventajas:**
- Rápido (frecuentemente mismo día)
- Comisiones bajas (€0-3)
- 36 países europeos

**Desventajas:**
- Solo EUR
- Solo Europa
- Algunos límites aplican

**Ideal para:**
- Usuarios europeos
- Pagos en EUR
- Transferencias regulares

## Elegir el método correcto

### Para usuarios de EE.UU.
- < $5,000: ACH
- > $5,000 o urgente: Wire

### Para usuarios europeos
- SEPA para todo en EUR
- Wire para otras monedas

### Para usuarios de México
- SPEI para todos los pagos en MXN
    `.trim(),
  },
  {
    id: "wd-005",
    slug: "payout-processing-times",
    title: "Tiempos de procesamiento de pagos",
    description: "Cuánto tardan los pagos en llegar a tu banco",
    category: "withdrawals",
    order: 5,
    keywords: ["procesamiento", "tiempo", "duración", "espera", "velocidad"],
    content: `
# Tiempos de procesamiento de pagos

Entiende cuándo esperar los fondos en tu cuenta bancaria.

## Etapas de procesamiento

### 1. Inicio (Instantáneo)
- Solicitud de pago enviada
- Verificaciones de cumplimiento realizadas
- Estado: "Procesando"

### 2. Envío al banco (0-4 horas)
- Enviado al procesador de pagos
- El banco recibe la instrucción
- Estado: "Enviado"

### 3. Procesamiento bancario (Variable)
- El banco procesa la transferencia
- Depende del método de pago
- Estado: "En tránsito"

### 4. Liquidación (Completo)
- Fondos en tu cuenta
- Estado: "Completado"

## Tiempos esperados

| Método | Procesamiento Supabank | Procesamiento bancario | Total |
|--------|------------------------:|----------------------:|------:|
| ACH | Mismo día | 2-3 días | 2-3 días |
| Wire | Mismo día | 1-2 días | 1-2 días |
| SEPA | Mismo día | 1 día | 1 día |
| SPEI | Mismo día | Mismo día | Mismo día |

## Días hábiles

- El procesamiento ocurre de lunes a viernes
- Excluye días festivos bancarios
- Aplican horas de corte
- Las solicitudes del fin de semana se procesan el lunes

## Horas de corte

| Método | Hora de corte (ET) | Procesamiento mismo día |
|--------|--------------------|-----------------------|
| ACH | 4:00 PM | Si es antes del corte |
| Wire | 4:00 PM | Si es antes del corte |
| SEPA | 2:00 PM CET | Si es antes del corte |
| SPEI | 5:00 PM CT | Si es antes del corte |

## Retrasos

Los pagos pueden retrasarse por:
- Primeros montos grandes
- Cuentas bancarias nuevas
- Verificación adicional necesaria
- Días festivos bancarios

## Seguimiento de tu pago

1. Ve a Transacciones
2. Encuentra la transacción de pago
3. Ver estado detallado
4. Ver llegada estimada
    `.trim(),
  },
  {
    id: "wd-006",
    slug: "failed-payouts-troubleshooting",
    title: "Solución de problemas de pagos fallidos",
    description: "Qué hacer cuando un pago falla",
    category: "withdrawals",
    order: 6,
    keywords: ["fallido", "error", "rechazado", "devuelto", "solución"],
    content: `
# Solución de problemas de pagos fallidos

A veces los pagos no se completan correctamente. Aquí te explicamos cómo resolver problemas.

## Razones comunes de fallo

### 1. Datos bancarios incorrectos
- Número de cuenta incorrecto
- Número de routing inválido
- Errores en el IBAN

**Solución:** Verifica y vuelve a introducir los datos bancarios

### 2. Nombre de cuenta no coincide
- El nombre no coincide con tu perfil de Supabank
- Problemas con cuenta conjunta
- Cuenta empresarial vs personal

**Solución:** Usa una cuenta a tu nombre verificado

### 3. Rechazo del banco
- Cuenta cerrada
- Cuenta congelada
- El banco no acepta transferencias

**Solución:** Contacta con tu banco o usa otra cuenta

### 4. Límite excedido
- Límite diario/mensual alcanzado
- Transacción individual muy grande

**Solución:** Espera al reinicio del límite o divide el pago

## Qué pasa con los pagos fallidos

1. **Aviso de devolución** - Se nos notifica del rechazo
2. **Fondos acreditados** - El dinero vuelve a tu saldo
3. **Notificación** - Recibes notificación por correo
4. **Listo para reintentar** - Puedes intentarlo de nuevo

## Plazos de devolución

| Método | Tiempo de devolución |
|--------|---------------------|
| ACH | 2-3 días hábiles |
| Wire | 1-2 días hábiles |
| SEPA | 1 día hábil |

## Resolver problemas

### Paso 1: Verificar datos
1. Ve a Ajustes > Pagos
2. Revisa los datos de la cuenta bancaria
3. Compara con tu estado de cuenta
4. Corrige cualquier error

### Paso 2: Contactar con el banco
- Verifica que la cuenta pueda recibir transferencias
- Comprueba si hay restricciones
- Confirma que la cuenta está activa

### Paso 3: Reintentar el pago
1. Asegúrate de que los datos son correctos
2. Inicia un nuevo pago
3. Monitorea el estado

### Paso 4: Contactar con soporte
Si los problemas persisten:
- Envía correo a support@supabank.xyz
- Incluye: ID de transacción, mensaje de error
- Investigaremos con el procesador de pagos
    `.trim(),
  },
  {
    id: "wd-007",
    slug: "payout-fees-explained",
    title: "Explicación de las comisiones de pago",
    description: "Entendiendo las comisiones de retiro",
    category: "withdrawals",
    order: 7,
    keywords: ["comisiones", "costo", "cargos", "precios", "retiro"],
    content: `
# Explicación de las comisiones de pago

Desglose transparente de todas las comisiones relacionadas con pagos.

## Estructura de comisiones

### Comisiones de Supabank

| Método | Nuestra comisión |
|--------|------------------|
| ACH | $0 |
| Wire doméstico | $15 |
| Wire internacional | $25 |
| SEPA | €0 |
| SPEI | $0 |

### Comisiones de conversión

Cuando el cripto se convierte a fiat:
- **Comisión de plataforma:** 2.0% del monto de conversión
- **Comisión de Bridge:** ~0.1% del monto de conversión
- **Total:** ~2.1% del monto de conversión

Todas las comisiones se muestran antes de confirmar cualquier transacción.

## Comisiones de terceros

### Tu banco puede cobrar

| Tipo de comisión | Monto |
|------------------|-------|
| Comisión por wire entrante | $10-25 |
| Comisión transferencia internacional | $15-50 |
| Conversión de moneda | 1-3% |

*Consulta con tu banco sus comisiones específicas*

### Bancos intermediarios

Para wires internacionales:
- Pueden deducir comisiones en el camino
- $10-30 típico
- Cargos de la red SWIFT

## Ejemplos de comisiones

### Ejemplo 1: ACH en EE.UU.
- Monto del pago: $1,000
- Comisión Supabank: $0
- Comisión bancaria: $0 (típicamente)
- **Recibes: $1,000**

### Ejemplo 2: Wire internacional
- Monto del pago: $10,000
- Comisión Supabank: $25
- Intermediario: ~$20
- Tu banco: ~$15
- **Recibes: ~$9,940**

### Ejemplo 3: SEPA
- Monto del pago: €5,000
- Comisión Supabank: €0
- Tu banco: €0-3
- **Recibes: €4,997-5,000**

## Minimizar comisiones

**Mejores prácticas:**
- Usa ACH para EE.UU. (gratis)
- Usa SEPA para Europa (gratis)
- Consolida pagos pequeños
- Elige bancos con bajas comisiones entrantes
    `.trim(),
  },
  {
    id: "wd-008",
    slug: "withdrawal-limits",
    title: "Límites de retiro",
    description: "Entendiendo tus límites de pago",
    category: "withdrawals",
    order: 8,
    keywords: ["límites", "máximo", "diario", "mensual", "restricción"],
    content: `
# Límites de retiro

Tus límites de pago dependen de tu nivel de verificación e historial de cuenta.

## Niveles de límites

### Estándar (Predeterminado)
- Por transacción: $10,000
- Diario: $25,000
- Mensual: $100,000

### Avanzado
- Por transacción: $50,000
- Diario: $100,000
- Mensual: $500,000

### Enterprise
- Por transacción: Personalizado
- Diario: Personalizado
- Mensual: Personalizado

## Cómo se calculan los límites

### Límites diarios
- Se reinician a medianoche UTC
- Incluye todos los pagos
- En todas las cuentas bancarias

### Límites mensuales
- Mes calendario
- Se reinicia el 1 de cada mes
- Acumulativo en todos los métodos

## Verificar tus límites

1. Ve a Ajustes > Límites
2. Ver nivel actual
3. Ver límites restantes
4. Seguimiento del uso

## Aumentar límites

### Estándar → Avanzado
Requisitos:
- 30 días de antigüedad de cuenta
- Transacciones exitosas
- Verificación adicional:
  - Comprobante de domicilio
  - Origen de fondos

### Avanzado → Enterprise
Requisitos:
- Cuenta empresarial
- Historial de alto volumen
- Revisión dedicada
- Contactar con ventas

## Retenciones de límites

Pueden ocurrir reducciones temporales de límites por:
- Cuentas nuevas
- Patrones de actividad inusuales
- Verificación pendiente
- Preocupaciones de seguridad

## Consejos

**Mejores prácticas:**
- Planifica retiros grandes con anticipación
- Verifica antes del reinicio de límites
- Contacta con soporte para aumentos
- Usa múltiples transacciones más pequeñas si es necesario
    `.trim(),
  },
];
